<template>
  <section class="sequence-section">
    <div class="section-heading">
      <div class="flex flex-column gap-1">
        <span
          ><strong>In Review: </strong> {{ steps.filter(item => item.review?.status).length }} /
          {{ steps.filter(item => item.status === 'completed').length }}</span
        >
        <span
          ><strong>{{ steps.filter(item => item.review?.status === 'approved').length }}</strong> Approved &
          <strong> {{ steps.filter(item => item.review?.status === 'declined').length }}</strong> Declined</span
        >
      </div>

      <div class="flex flex-column gap-1" v-if="steps.some(item => item.review?.status !== 'approved')">
        <a-button type="primary" ghost @click="$emit('approve-all')"> Approve All</a-button>
        <p class="sequence-muted m-0">Use this when every step looks correct.</p>
      </div>
    </div>

    <div class="step-list">
      <article
        class="step-card"
        :class="{ 'step-card--declined': step.review?.status === 'declined' }"
        v-for="step in steps"
        :key="step.id"
      >
        <div class="step-card__header">
          <div>
            <span class="step-title">
              <a-badge :number-style="{ backgroundColor: '#2563eb' }" :count="step.number" :overflowCount="1000" />
              {{ stepName(step) }}
            </span>
            <div class="sequence-muted">{{ stepTypeTitle(step) }}</div>
          </div>
          <div class="step-statuses">
            <a-tag :color="step.status === 'completed' ? '#10b981' : '#f59e0b'" class="m-0">
              {{ SEQUENCE_STEP_STATUS[step.status]?.title || step.status || 'Unknown' }}
            </a-tag>
            <a-tag :color="reviewStatusColor(step.review?.status)" class="m-0">
              {{ reviewStatusTitle(step.review?.status) }}
            </a-tag>
          </div>
        </div>

        <div class="step-card__actions">
          <a-button ghost type="primary" @click="$emit('open-step', step)">
            {{ step.review?.status ? evidenceActionLabel(step) : 'Review' }}
          </a-button>
          <a-button type="primary" @click="$emit('approve-step', step)">Approve</a-button>
          <a-button type="danger" ghost @click="$emit('decline-step', step)">Decline</a-button>
        </div>

        <template v-if="step.review?.status === 'declined'">
          <label class="decline-reason">
            <span>Reason for decline</span>
            <a-input
              :value="step.review.comment"
              placeholder="Explain what needs to be fixed."
              @input="$emit('update-decline-reason', step, $event.target.value)"
            />
          </label>
          <p v-if="!step.review.comment" class="validation-copy">Add a reason before declining this step.</p>
          <p v-else class="m-0">Reason: {{ step.review.comment }}</p>
        </template>
      </article>
    </div>

    <p v-if="showValidation" class="validation-copy">Review all required steps to continue.</p>
  </section>
</template>

<script>
import { Badge, Button, Input, Tag } from 'ant-design-vue'
import {
  getEvidenceTitle,
  getReviewStatusColor,
  getReviewStatusTitle,
  getStepName,
  getStepTypeTitle,
  SEQUENCE_STEP_STATUS
} from './sequence-review.helpers'

export default {
  name: 'SequenceReviewAssociateSteps',
  components: {
    'a-badge': Badge,
    'a-button': Button,
    'a-input': Input,
    'a-tag': Tag
  },
  props: {
    steps: {
      type: Array,
      default: () => []
    },
    showValidation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      SEQUENCE_STEP_STATUS
    }
  },
  methods: {
    stepName: getStepName,
    stepTypeTitle: getStepTypeTitle,
    evidenceActionLabel: getEvidenceTitle,
    reviewStatusTitle: getReviewStatusTitle,
    reviewStatusColor: getReviewStatusColor
  }
}
</script>

<style scoped>
.sequence-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.step-list {
  display: grid;
  grid-auto-rows: max-content;
  gap: 12px;
  max-height: 360px;
  overflow: auto;
}

.step-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
}

.step-card--declined {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.04);
}

.step-card__header,
.step-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.step-card__actions {
  justify-content: flex-end;
  flex-wrap: wrap;
}

.step-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.step-statuses {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.decline-reason {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-item span,
.sequence-muted {
  color: #6b7280;
  font-size: 12px;
}

.validation-copy {
  color: #ef4444;
}

@media screen and (max-width: 640px) {
  .step-card__header,
  .step-card__actions,
  .section-heading {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
